// compose.js: the pure, seeded exam draw. Per section: pick a CALC count inside the blueprint
// band (clamped to the CALC lessons available), then fill each CALC/GEN slot by syllabus
// heading (probability ∝ its TP 12880 item count), then a lesson uniformly in that heading
// (preferring lessons not in `recent`, PLAN §5.2 anti-repeat; heading weights are untouched),
// then one of that lesson's generators. No lesson repeats within a section. Pool, blueprint and
// syllabus are arguments so tests can inject synthetic ones; exam.js binds the real ones.

import { makeRng } from './prng.js';
import { instanceSeed } from './draw.js';

const other = (type) => (type === 'CALC' ? 'GEN' : 'CALC');

// Pick a value with probability ∝ weight. entries: [value, weight > 0][].
function weightedPick(rng, entries) {
  let r = rng.next() * entries.reduce((sum, [, w]) => sum + w, 0);
  return entries.find(([, w]) => (r -= w) < 0)[0];
}

// Heading by item weight → lesson uniform (not-recent first) → generator uniform. Headings with
// no items never draw. Undefined when nothing is drawable (consumes no rng then).
function pickGenerator(rng, gens, headings, recent) {
  const byHeading = Map.groupBy(
    gens.filter((g) => headings[g.syllabus]?.items > 0),
    (g) => g.syllabus,
  );
  if (!byHeading.size) return undefined;
  const heading = weightedPick(rng, [...byHeading.keys()].map((h) => [h, headings[h].items]));
  const lessons = [...new Set(byHeading.get(heading).map((g) => g.topic))];
  const fresh = lessons.filter((l) => !recent.has(l));
  const lesson = rng.pick(fresh.length ? fresh : lessons);
  return rng.pick(byHeading.get(heading).filter((g) => g.topic === lesson));
}

// Slot of `type`: prefer unused lessons of that type, then of the other type, then (pool
// exhausted) reuse a lesson rather than leave the section short.
function drawSlot(rng, gens, headings, recent, used, type) {
  const attempts = [[type, used], [other(type), used], [type, new Set()], [other(type), new Set()]];
  for (const [t, u] of attempts) {
    const g = pickGenerator(rng, gens.filter((x) => x.type === t && !u.has(x.topic)), headings, recent);
    if (g) return g;
  }
  return undefined;
}

function composeSection(gens, { total, calc: [lo, hi] }, headings, recent, seed) {
  const rng = makeRng(seed);
  const calcLessons = new Set(gens.filter((g) => g.type === 'CALC').map((g) => g.topic)).size;
  const nCalc = Math.min(rng.int(lo, hi), calcLessons);
  // Shuffling the slot types shuffles the question order too, so CALC never clusters first.
  const slots = rng.shuffle([...Array(nCalc).fill('CALC'), ...Array(total - nCalc).fill('GEN')]);
  const { picks } = slots.reduce(
    ({ used, picks }, type) => {
      const g = drawSlot(rng, gens, headings, recent, used, type);
      return g ? { used: new Set(used).add(g.topic), picks: [...picks, g] } : { used, picks };
    },
    { used: new Set(), picks: [] },
  );
  // Carry the generator's stamped sources so exam answer-review stays traceable.
  return picks.map((g, i) => ({ ...g.generate(makeRng(instanceSeed(seed, i))), sources: g.sources }));
}

/**
 * @param {{ section:string, topic:string, type:'CALC'|'GEN', syllabus:string, sources?:string[], generate:Function }[]} pool
 * @param {Record<string,{ total:number, calc:[number,number] }>} blueprint
 * @param {Record<string,Record<string,{ items:number }>>} syllabus
 * @param {number} masterSeed
 * @param {Set<string>} [recent] recently-drawn lesson topics, de-prioritized within a heading
 * @returns {import('./contract.js').Question[]} sections in blueprint order
 */
export const composeExam = (pool, blueprint, syllabus, masterSeed, recent = new Set()) =>
  Object.entries(blueprint).flatMap(([section, band], s) =>
    composeSection(
      pool.filter((g) => g.section === section),
      band,
      syllabus[section] ?? {},
      recent,
      instanceSeed(masterSeed, s),
    ),
  );
